(function () {
  'use strict';

  const API_BASE = 'https://tangent-crosssell-production.up.railway.app';

  let membershipsCache = null;  // product handle → [collection handles]
  let isChecking = false;

  // ─── Engine (inlined from cross-sell-engine.js) ───────────────────────────

  function findCrossSells(cartItems, rules) {
    const cartHandles = new Set(cartItems.map(i => i.productHandle));
    const cartCollections = new Set(cartItems.flatMap(i => i.collectionHandles));

    const suggestions = [];

    for (const rule of rules.filter(r => r.type === 'product')) {
      if (cartHandles.has(rule.triggerHandle) && !cartHandles.has(rule.suggestHandle)) {
        suggestions.push({ type: 'product', ruleId: rule.id, suggestHandle: rule.suggestHandle, message: rule.message });
      }
    }

    const suggestedCollections = new Set();
    for (const rule of rules.filter(r => r.type === 'collection')) {
      if (
        cartCollections.has(rule.triggerHandle) &&
        !cartCollections.has(rule.suggestHandle) &&
        !suggestedCollections.has(rule.suggestHandle)
      ) {
        suggestions.push({ type: 'collection', ruleId: rule.id, suggestHandle: rule.suggestHandle, message: rule.message });
        suggestedCollections.add(rule.suggestHandle);
      }
    }

    return suggestions;
  }

  // ─── Data Fetching ────────────────────────────────────────────────────────

  async function getMemberships() {
    if (membershipsCache) return membershipsCache;
    membershipsCache = await fetch(`${API_BASE}/api/memberships`).then(r => r.json());
    return membershipsCache;
  }

  async function getRules() {
    return fetch(`${API_BASE}/api/rules`).then(r => r.json());
  }

  async function getProductData(handle) {
    const res = await fetch(`/products/${handle}.js`);
    if (!res.ok) return null;
    return res.json();
  }

  // ─── Tracking ─────────────────────────────────────────────────────────────

  function track(event, ruleId) {
    fetch(`${API_BASE}/api/track`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ event, ruleId }),
    });
  }

  // ─── Rendering ────────────────────────────────────────────────────────────

  function formatPrice(cents) {
    return (cents / 100).toLocaleString('en-US', { style: 'currency', currency: 'USD' });
  }

  function renderCollectionSuggestion(s) {
    return `
      <div class="tcs-suggestion" data-type="collection">
        <span class="tcs-message">${s.message}</span>
        <a class="tcs-btn" href="/collections/${s.suggestHandle}" data-rule-id="${s.ruleId}">
          Shop Now
        </a>
      </div>
    `;
  }

  function renderProductSuggestion(s, product) {
    const variant = product.variants.find(v => v.available) || product.variants[0];
    const image = product.featured_image || '';
    const price = formatPrice(product.price);
    return `
      <div class="tcs-suggestion" data-type="product">
        ${image ? `<img class="tcs-img" src="${image}&width=80" alt="${product.title}" width="40" height="40">` : ''}
        <span class="tcs-message">${s.message}</span>
        <span class="tcs-meta">${product.title} &mdash; ${price}</span>
        <button class="tcs-btn" data-rule-id="${s.ruleId}" data-variant-id="${variant.id}">
          Add to Cart
        </button>
      </div>
    `;
  }

  function getContainers() {
    return document.querySelectorAll('.tangent-crosssell-container');
  }

  function clearContainers() {
    getContainers().forEach(el => { el.innerHTML = ''; el.hidden = true; });
  }

  async function renderSuggestions(suggestions) {
    const enriched = await Promise.all(
      suggestions.map(async s => {
        if (s.type === 'product') {
          const product = await getProductData(s.suggestHandle);
          return product ? { ...s, product } : null;
        }
        return s;
      })
    );

    const valid = enriched.filter(Boolean);
    if (!valid.length) { clearContainers(); return; }

    const html = valid.map(s =>
      s.type === 'collection' ? renderCollectionSuggestion(s) : renderProductSuggestion(s, s.product)
    ).join('');

    getContainers().forEach(el => {
      el.innerHTML = html;
      el.hidden = false;
    });

    valid.forEach(s => track('impression', s.ruleId));
  }

  // ─── Main Check ───────────────────────────────────────────────────────────

  async function checkCart() {
    if (isChecking) return;
    isChecking = true;
    try {
      const [cart, memberships, rules] = await Promise.all([
        fetch('/cart.js').then(r => r.json()),
        getMemberships(),
        getRules(),
      ]);

      if (!cart.items.length || !rules.length) { clearContainers(); return; }

      const cartItems = cart.items.map(item => ({
        productHandle: item.handle,
        collectionHandles: memberships[item.handle] || [],
      }));

      const suggestions = findCrossSells(cartItems, rules);
      if (!suggestions.length) { clearContainers(); return; }

      await renderSuggestions(suggestions);
    } catch (err) {
      console.warn('[tangent-crosssell] Error:', err);
      clearContainers();
    } finally {
      isChecking = false;
    }
  }

  // ─── Event Wiring ─────────────────────────────────────────────────────────

  function wireEvents() {
    // Add to cart — delegated so it works in both drawer and cart page
    document.addEventListener('click', async function (e) {
      const btn = e.target.closest('.tcs-btn[data-variant-id]');
      if (!btn) return;

      const variantId = parseInt(btn.dataset.variantId, 10);
      const ruleId = btn.dataset.ruleId;
      const suggestion = btn.closest('.tcs-suggestion');

      track('click', ruleId);
      btn.disabled = true;
      btn.textContent = 'Adding…';

      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: variantId, quantity: 1 }),
      });

      if (res.ok) {
        document.dispatchEvent(new CustomEvent('cart:refresh'));
        suggestion.remove();
      } else {
        btn.disabled = false;
        btn.textContent = 'Try again';
      }
    });

    // Collection link click tracking
    document.addEventListener('click', function (e) {
      const link = e.target.closest('.tcs-btn[data-rule-id]:not([data-variant-id])');
      if (link) track('click', link.dataset.ruleId);
    });

    // Dawn pub-sub
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, checkCart);
    }
    document.addEventListener('cart:updated', checkCart);
    document.addEventListener('cart:refresh', checkCart);

    // District jQuery cart fallback
    if (typeof jQuery !== 'undefined') {
      jQuery(document).ajaxComplete(function (event, xhr, settings) {
        if (settings.url && /\/cart\/(add|change|update)/.test(settings.url)) {
          checkCart();
        }
      });
    }
  }

  // ─── Init ─────────────────────────────────────────────────────────────────

  function init() {
    wireEvents();
    if (getContainers().length) checkCart();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
